import { useContext, useEffect, useRef, useState } from 'react'
import { SideModal } from 'ui/molecules/SideModal'
import { AddLocationModalProps } from './types'
import { Button, Typography, FormContext, Input } from '@frontend/design_system'
import { MAP_MODAL, LOCATION_ADDING_FORM_VALUES } from 'texts/locationDetails'
import {
  Autocomplete,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api'
import { EnvironmentVariablesService } from 'utils/env'
import { getAddressComponents } from './utils'
import { ContextType } from 'ui/molecules/Form/types'
import { setIsNewLocation } from 'redux/locations/slice'
import { useDispatch } from 'react-redux'
import { MAP_OPTIONS, MAX_FRACTIONAL_PART, libraries } from 'constants/map'
import { CANCEL } from 'texts/uiTexts'
import { checkAreCitiesSimilar } from 'utils/checkIsCitySimilar'
import { cityRegex } from 'constants/regex'
import { CITIES_WITHOUT_COORDINATES } from './constants'
import styles from './styles.module.scss'

const googleMapsApiKey = EnvironmentVariablesService.getEnv(
  'REACT_APP_GOOGLE_API_KEY'
)

export const CONTAINER_STYLE = {
  width: '100%',
  height: '280px',
  marginBottom: 24,
  borderRadius: 12,
}

export const AddLocationModal = ({
  currentLocation,
  states,
  handleStateChange,
  handleCityChange,
  cities,
}: AddLocationModalProps) => {
  const { country, city, state } = currentLocation
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [place, setPlace] = useState<google.maps.places.PlaceResult | null>(
    null
  )
  const [isModalOpen, setModalOpen] = useState(false)
  const [position, setPosition] = useState<{ lat: number; lng: number }>()
  const [address, setAddress] = useState<string>('')
  const { setValues, setErrors } = useContext<ContextType>(FormContext)
  const dispatch = useDispatch()

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey,
    libraries,
  })

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete
  }

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace()
      if (place.geometry?.location) {
        const newPosition = {
          lat: place.geometry.location?.lat(),
          lng: place.geometry.location?.lng(),
        }
        setPosition(newPosition)
        setPlace(place)
        setAddress(place.formatted_address || '')
        map?.panTo(newPosition)
      }
    }
  }

  const getAddressFromCoords = async (lat: number, lng: number) => {
    const geocoder = new google.maps.Geocoder()
    const res = await geocoder.geocode({ location: { lat, lng } })
    if (res.results && res.results.length > 0) {
      const newPlace = res.results[0]
      const placeResult: google.maps.places.PlaceResult = {
        address_components: newPlace.address_components,
        formatted_address: newPlace.formatted_address,
        geometry: newPlace.geometry,
        name: newPlace.address_components?.[0]?.long_name || '',
        place_id: newPlace.place_id,
      }
      setPlace(placeResult)
      setAddress(newPlace.formatted_address || '')
      if (inputRef.current) {
        inputRef.current.value = newPlace.formatted_address || ''
      }
    }
  }

  const onMarkerDragEnd = async (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const newLat = event.latLng.lat()
      const newLng = event.latLng.lng()
      setPosition({ lat: newLat, lng: newLng })
      await getAddressFromCoords(newLat, newLng)
    }
  }

  const handleOpenModal = () => {
    setModalOpen(true)
    dispatch(setIsNewLocation(true))
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setAddress('')
  }

  const handleAddLocation = () => {
    if (place) {
      const addressComponents = getAddressComponents(
        place,
        country.name,
        city.name
      )
      if (addressComponents) {
        const {
          city: cityFromAddress,
          street,
          building,
          postalCode,
          lat,
          lng,
          country: countryFromAddress,
          state,
        } = addressComponents

        setErrors((prevErrors) => ({
          ...prevErrors,
          [LOCATION_ADDING_FORM_VALUES.COUNTRY.ID]:
            country.name !== countryFromAddress
              ? LOCATION_ADDING_FORM_VALUES.COUNTRY.INCORRECT_COUNTRY_MESSAGE
              : '',
        }))

        const currentState = states.find((el) => el.label === state)
        let cityFromList = cities.find((el) => el.label === cityFromAddress)

        if (!cityFromList && !cityRegex.test(cityFromAddress)) {
          cityFromList = cities.find((el) =>
            checkAreCitiesSimilar(el.label, cityFromAddress)
          )
        }

        let selectedCity = ''
        let selectedCityValue = ''

        if (!cityRegex.test(cityFromAddress)) {
          if (checkAreCitiesSimilar(city.name, cityFromAddress)) {
            selectedCity = city.name
            selectedCityValue = cityFromList?.value || city.id
          } else {
            selectedCity = cityFromList?.label || city.name
            selectedCityValue = cityFromList?.value || city.id
          }
        } else {
          selectedCity = cityFromAddress
          selectedCityValue = cityFromList?.value || ''
        }

        setValues((prevValues) => ({
          ...prevValues,
          [LOCATION_ADDING_FORM_VALUES.STATE.ID]: currentState?.value,
          [LOCATION_ADDING_FORM_VALUES.LATITUDE.ID]:
            lat?.toFixed(MAX_FRACTIONAL_PART),
          [LOCATION_ADDING_FORM_VALUES.LONGITUDE.ID]:
            lng?.toFixed(MAX_FRACTIONAL_PART),
          [LOCATION_ADDING_FORM_VALUES.CITY.ID]: selectedCity,
          [LOCATION_ADDING_FORM_VALUES.STREET.ID]: street,
          [LOCATION_ADDING_FORM_VALUES.BUILDING.ID]: building,
          [LOCATION_ADDING_FORM_VALUES.POSTAL_CODE.ID]: postalCode,
          ...(selectedCity !== city.name && {
            [LOCATION_ADDING_FORM_VALUES.NEW_LOCATION_TYPE.ID]: '',
            [LOCATION_ADDING_FORM_VALUES.LOCATION.ID]: '',
          }),
        }))
        if (currentState) {
          handleStateChange(currentState)
        }
        handleCityChange({
          label: selectedCity,
          value: selectedCityValue,
        })

        handleCloseModal()
      }
    }
  }

  useEffect(() => {
    const getCoordsByCountry = async () => {
      const geocoder = new google.maps.Geocoder()
      const res = await geocoder.geocode({
        address: `${country.name} ${state.name} ${
          CITIES_WITHOUT_COORDINATES[city.name] || city.name
        }`,
      })
      if (res.results[0]) {
        const {
          geometry: { location },
        } = res.results[0]

        setPosition({
          lat: +location.lat(),
          lng: +location.lng(),
        })
      }
    }
    isLoaded && getCoordsByCountry()
  }, [currentLocation, isLoaded])

  return (
    <>
      <Typography
        Tag="div"
        name="body2WMedium"
        color="grey500"
        className={styles['add-location']}
      >
        {MAP_MODAL.BUTTON_QUESTION}
        <div className={styles['add-location-button']}>
          <Button
            size="small"
            typographyName="body2WBold"
            variant="link"
            isInline
            label={MAP_MODAL.BUTTON_LABEL}
            disabled={!country.name}
            onClick={handleOpenModal}
          />
        </div>
      </Typography>
      {isModalOpen && (
        <SideModal onClose={handleCloseModal} title={MAP_MODAL.TITLE}>
          <div className={styles['modal-info-content']}>
            <div>
              {isLoaded && (
                <>
                  <GoogleMap
                    mapContainerStyle={CONTAINER_STYLE}
                    center={position}
                    zoom={12}
                    options={MAP_OPTIONS}
                    onLoad={(mapInstance: google.maps.Map) =>
                      setMap(mapInstance)
                    }
                  >
                    <Marker
                      position={position || { lat: 0, lng: 0 }}
                      onDragEnd={onMarkerDragEnd}
                      draggable
                      onClick={onMarkerDragEnd}
                    />
                  </GoogleMap>
                  <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                    <Input
                      label={MAP_MODAL.INPUT_LABEL}
                      placeholder={MAP_MODAL.INPUT_PLACEHOLDER}
                      defaultValue={address}
                      type="text"
                      size="large"
                      ref={inputRef}
                    />
                  </Autocomplete>
                </>
              )}
            </div>
            <div className={styles['modal-buttons']}>
              <Button
                label={CANCEL}
                variant="secondary-border"
                size="large"
                onClick={handleCloseModal}
              />
              <Button
                label={MAP_MODAL.CONFIRM_BUTTON_LABEL}
                size="large"
                onClick={handleAddLocation}
              />
            </div>
          </div>
        </SideModal>
      )}
    </>
  )
}
